import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import useAutoFocus from 'hooks/useAutoFocus'
import useResources from 'hooks/useResources'
import { color } from 'styles/colors'
import { screenSize, PageHeader, PageTitle } from 'styles/shared'
import Tabs from 'components/Tabs'
import ResourceTable from './ResourceTable'
import AddButton from 'components/AddButton'
import ResourceFormModal from 'pages/ResourceAdmin/ResourceFormModal'
import Select from 'components/Select'
import Resource, { ValidResourceTypes, EditableResourceProps } from 'types/Resource'

type ResourceProps = {
  type: ValidResourceTypes
  resource: Resource
}
const BRAND_OPTIONS = [[
  { value: 'trane_home', name: 'Trane' },
  { value: 'asair_home', name: 'American Standard' }
]]
const TABS = {
  0: 'video',
  1: 'article',
  2: 'faq',
  3: 'thermostat'
}
const ResourceAdmin = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [selectedBrand, setSelectedBrand] = useState('trane_home')
  const [isResourceFormModalDisplayed, setIsResourceFormModalDisplayed] = useState(false)
  const { thermostatReleases, articles, faqs, videos, areResourcesLoaded } = useResources({ brand: selectedBrand, publishedOnly: false, refreshTimestamp })
  const [isEdit, setIsEdit] = useState(false)
  const [editableResource, setEditableResource] = useState<EditableResourceProps>({
    incomingType: TABS[activeTabIndex],
    incomingResource: null
  })
  const title = useRef(null)
  useAutoFocus({ element: title })
  const onSuccessfulSubmission = () => {
    setRefreshTimestamp(Date.now())
    setIsEdit(false)
    setEditableResource({
      incomingType: TABS[activeTabIndex],
      incomingResource: null
    })
  }
  const onCloseCallback = () => {
    setIsEdit(false)
    setIsResourceFormModalDisplayed(false)
    setEditableResource({
      incomingType: TABS[activeTabIndex],
      incomingResource: null
    })
  }
  const onClickAddResource = (e:React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    setEditableResource({
      incomingType: TABS[activeTabIndex],
      incomingResource: null
    })
    setIsEdit(false)
    setIsResourceFormModalDisplayed(true)
  }
  const onClickEditResource = (props:ResourceProps) => {
    const { resource, type } = props || {}
    setEditableResource({
      incomingType: type,
      incomingResource: resource as Resource
    })
    setIsEdit(true)
    setIsResourceFormModalDisplayed(true)
  }

  return (
    <S.Container>
      <S.PageHeader>
        <PageTitle ref={title}>Resources</PageTitle>
      </S.PageHeader>

      <S.Body>
        <S.Select
          label="Brand"
          selectOptions={BRAND_OPTIONS}
          value={selectedBrand}
          onChange={(e) => { setSelectedBrand(e.target.value) }}
        />

        <S.AddButton
          uri="/admin/resources"
          onClick={onClickAddResource}
        >
          Add Resource
        </S.AddButton>
        {isResourceFormModalDisplayed &&
          <ResourceFormModal
            isDisplayed={isResourceFormModalDisplayed}
            closeCallback={onCloseCallback}
            onSuccessfulSubmission={onSuccessfulSubmission}
            isEdit={isEdit}
            editableResource={editableResource}
            activeTab={TABS[activeTabIndex]}
          />}

        <Tabs
          activeTabIndex={activeTabIndex}
          onSelectTabIndexCallback={(index) => { setActiveTabIndex(index) }}
          tabItems={[
            {
              key: '0',
              header: 'Videos',
              details: (
                <ResourceTable
                  type="video"
                  resources={videos}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            },
            {
              key: '1',
              header: 'Articles',
              details: (
                <ResourceTable
                  type="article"
                  resources={articles}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            },
            {
              key: '2',
              header: 'FAQs',
              details: (
                <ResourceTable
                  type="faq"
                  resources={faqs}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            },
            {
              key: '3',
              header: <S.Header>Thermostat Releases</S.Header>,
              details: (
                <ResourceTable
                  type="thermostat"
                  resources={thermostatReleases}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            }
          ]}
        />
      </S.Body>

    </S.Container>
  )
}

const S = {
  Container: styled.div`
    width: 100%;
  `,
  Body: styled.div`
    margin: 10px;
    padding: 10px;
    background-color: ${color('paneBackground')};
    display: flex;
    flex-direction: column;
  `,
  PageHeader: styled(PageHeader)`
    @media ${screenSize.small} {
      display: none;
    }
  `,
  AddButton: styled(AddButton)`
    align-self: end;
  `,
  Select: styled(Select)`
    margin-bottom: 10px;
    width: 200px;
  `,
  Header: styled.div`
    padding: 0 15px;
  `
}

export default ResourceAdmin
